// Production environment configuration - NO FALLBACK VALUES for security
// All required environment variables must be set, or the app will fail fast

interface EnvironmentConfig {
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId?: string;
  };
  api: {
    openWeatherMapApiKey?: string;
    noaaApiKey?: string;
  };
  stripe: {
    publishableKey: string;
  };
  features: {
    enableAnalytics: boolean;
    enableCrashReporting: boolean;
    debugMode: boolean;
  };
}

// Helper function to safely get env var with inline replacement support
// In web builds, webpack replaces process.env.VAR_NAME at build time
function safeGetEnvVar(value: string | undefined, varName: string, required: boolean = true): string {
  if (!value || value === 'undefined') {
    if (required) {
      const error = `Missing required environment variable: ${varName}`;
      console.error('[env.production]', error);
      // Don't throw in production web - log and use empty string to prevent white screen
      // The app will likely fail later, but at least we'll see error messages
      return '';
    }
    return '';
  }
  return value;
}

// Production configuration - use direct property access for webpack compatibility
const productionConfig: EnvironmentConfig = {
  firebase: {
    // Direct property access works with webpack's DefinePlugin
    apiKey: safeGetEnvVar(process.env.EXPO_PUBLIC_FIREBASE_API_KEY, 'EXPO_PUBLIC_FIREBASE_API_KEY'),
    authDomain: safeGetEnvVar(process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN, 'EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN'),
    projectId: safeGetEnvVar(process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID, 'EXPO_PUBLIC_FIREBASE_PROJECT_ID'),
    storageBucket: safeGetEnvVar(process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET, 'EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: safeGetEnvVar(process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID, 'EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID'),
    appId: safeGetEnvVar(process.env.EXPO_PUBLIC_FIREBASE_APP_ID, 'EXPO_PUBLIC_FIREBASE_APP_ID'),
    measurementId: process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID,
  },
  api: {
    // OpenWeatherMap is optional - will be undefined if not provided
    openWeatherMapApiKey: safeGetEnvVar(process.env.EXPO_PUBLIC_OPENWEATHERMAP_API_KEY, 'EXPO_PUBLIC_OPENWEATHERMAP_API_KEY', false),
    noaaApiKey: safeGetEnvVar(process.env.EXPO_PUBLIC_NOAA_API_KEY, 'EXPO_PUBLIC_NOAA_API_KEY', false),
  },
  stripe: {
    // Checkout is created by Cloud Functions; the web client does not use a
    // Stripe.js publishable key to start that flow.
    publishableKey: safeGetEnvVar(process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY, 'EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY', false),
  },
  features: {
    enableAnalytics: true,
    enableCrashReporting: true,
    debugMode: false,
  },
};

console.log('[env.production] Configuration loaded');
console.log('[env.production] Firebase project:', productionConfig.firebase.projectId);

export const env = productionConfig;
export type { EnvironmentConfig };
